import { useEffect, useRef } from "react";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  fmtBid,
  leadDate,
  leadKey,
  leadOwner,
  leadValue,
  tierColor,
  tierLetter,
} from "./leadUtils";
import type { DealLead, LeadSortKey, SortDir } from "./types";

interface LeadsTableProps {
  leads: DealLead[]; // visible (filtered + sorted) list
  hiddenCount: number;
  newOnlyExcluded: number | null; // "(new only · N on lists)" or null when off
  sortKey: LeadSortKey | null;
  sortDir: SortDir;
  onSort: (key: LeadSortKey) => void;
  selected: Set<string>;
  onToggleSelect: (key: string, visibleKeys: string[], shift: boolean) => void;
  onSelectAll: (visibleKeys: string[], checked: boolean) => void;
  newOnly: boolean;
  onNewOnlyChange: (value: boolean) => void;
  activeLeadKey: string | null;
  onRowClick: (lead: DealLead) => void;
  onHide: (key: string) => void;
  onUnhideAll: () => void;
}

const SORTABLE: { key: LeadSortKey; label: string }[] = [
  { key: "type", label: "Type" },
  { key: "address", label: "Address" },
  { key: "owner", label: "Owner" },
  { key: "date", label: "Date" },
  { key: "value", label: "Value" },
  { key: "tier", label: "Tier" },
];

export const LeadsTable = ({
  leads,
  hiddenCount,
  newOnlyExcluded,
  sortKey,
  sortDir,
  onSort,
  selected,
  onToggleSelect,
  onSelectAll,
  newOnly,
  onNewOnlyChange,
  activeLeadKey,
  onRowClick,
  onHide,
  onUnhideAll,
}: LeadsTableProps) => {
  const visibleKeys = leads.map(leadKey);
  const allSelected =
    visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  // Marker click → scroll the matching row into view.
  useEffect(() => {
    if (!activeLeadKey) return;
    rowRefs.current
      .get(activeLeadKey)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeLeadKey]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-3 py-2 text-sm">
        <span className="font-semibold">
          Prospects — {leads.length}{" "}
          {leads.length === 1 ? "property" : "properties"}
        </span>
        {newOnlyExcluded != null && (
          <span className="text-muted-foreground">
            (new only · {newOnlyExcluded} on lists)
          </span>
        )}
        {hiddenCount > 0 && (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={onUnhideAll}
          >
            Show {hiddenCount} hidden
          </button>
        )}
        <label className="ml-auto flex items-center gap-1.5">
          <Checkbox
            checked={newOnly}
            onCheckedChange={(v) => onNewOnlyChange(v === true)}
          />
          New only
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead className="w-8">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(v) => onSelectAll(visibleKeys, v === true)}
                  aria-label="Select all visible"
                />
              </TableHead>
              {SORTABLE.map(({ key, label }) => (
                <TableHead
                  key={key}
                  className="cursor-pointer select-none whitespace-nowrap"
                  onClick={() => onSort(key)}
                >
                  {label}
                  {sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
              ))}
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => {
              const key = leadKey(lead);
              const value = leadValue(lead);
              const letter = tierLetter(lead.dealTier);
              return (
                <TableRow
                  key={key}
                  ref={(el) => {
                    if (el) rowRefs.current.set(key, el);
                    else rowRefs.current.delete(key);
                  }}
                  className={cn(
                    "cursor-pointer",
                    key === activeLeadKey && "bg-accent",
                  )}
                  onClick={() => onRowClick(lead)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(key)}
                      onClick={(e) =>
                        onToggleSelect(key, visibleKeys, e.shiftKey)
                      }
                      aria-label={`Select ${lead.address ?? key}`}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {lead.kind === "auction" ? "🏛️ Auction" : "⚖️ Probate"}
                  </TableCell>
                  <TableCell className="max-w-64 truncate font-medium">
                    {lead.address ?? "Address pending"}
                  </TableCell>
                  <TableCell className="max-w-48 truncate">
                    {leadOwner(lead) ?? "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {leadDate(lead) ?? "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {value != null ? fmtBid(value) : "—"}
                  </TableCell>
                  <TableCell>
                    {letter ? (
                      <Badge
                        className="text-white border-transparent"
                        style={{ backgroundColor: tierColor(lead.dealTier) }}
                      >
                        {letter}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive"
                      title="Hide this row"
                      onClick={() => onHide(key)}
                    >
                      ✕
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
